import React, { useEffect, useMemo, useState, useRef } from "react"
import { graphql } from "gatsby"
import scrollTo from "gatsby-plugin-smoothscroll"

import globalStyles from "../services/globalStyles.module.scss"
import styles from "./features.module.scss"
import Layout from "../components/layout"
import Header from "../components/header"
import Footer from "../components/footer"
import TrackVisibility from "../components/trackVisibility"
import FeatureTMP from "../components/sportDetail/featureTMP"
import Landing from "../components/features/landing"
import GetStarted from "../components/getStarted"

import Button from "../components/button"
import FeaturesPanel from "../components/features/featuresPanel"
import {
  CollectionsIcon,
  CommunicationIcon,
  DiagramIcon,
  InjuryIcon,
  MonitoringIcon,
  PlanningIcon,
  VideoAnalysisIcon,
} from "../components/svgIcons"
import SeeHowItWorks from "../components/index/seeHowItWorks"

const featuresTexts = {
  title: "Everything in one place",
  sections: [
    {
      section_header: "Planning & Management",
      section_description:
        "Build programmes for teams and individuals and plan sessions with ease",
      texts: [
        {
          header: "Calendar",
          paragraph:
            "Generate a clear agenda for your players and staff for all types of events",
        },
        {
          header: "Session Builder",
          paragraph:
            "Build team sessions & individual workouts and add all of the information you require, such as location, meeting time or coaches notes.",
        },
        {
          header: "Attendance",
          paragraph:
            "Be in control of your players' attendance statistics and injury reporting. Mark attendance in seconds using our mobile app.",
        },
        {
          header: "Session Templates",
          paragraph:
            "Save time by building training templates of workouts that you can share with your athletes in just one click.",
        },
      ],
    },
    {
      section_header: "Monitoring & Evaluations",
      section_description:
        "Monitor testing data, information and athlete training loads. Make data-driven coaching decisions",
      texts: [
        {
          header: "Reports",
          paragraph:
            "Analyse your data like never before. Build customised reports for individuals or groups.",
        },
        {
          header: "Test Results",
          paragraph:
            "Access all of your testing data in one place. Upload directly to XPS from your GPS softwares or manually add results yourself.",
        },
        {
          header: "Forms & Questionnaires",
          paragraph:
            "Create high levels of accountability with your athletes. Gain feedback, testing data and information from your athletes in seconds.",
        },
      ],
    },
    {
      section_header: "Communication",
      section_description:
        "Keep all communications with coaches and athletes in one place",
      texts: [
        {
          header: "Messages Platform",
          paragraph:
            "Send messages in personal or group chats and stay in touch with your athletes.",
        },
        {
          header: "Share in a Second",
          paragraph:
            "Share videos, animations, photos, documents or test results from XPS Collections or your own device in seconds.",
        },
      ],
    },
    {
      section_header: "Tactics & Diagrams",
      section_description:
        "Recreate game and training situations with sport specific diagrams and animations",
      texts: [
        {
          header: "XPS Playbook",
          paragraph:
            "Build static diagrams or flexible animations in a user-friendly environment to illustrate your strategies across your network.",
        },
        {
          header: "Sports Flexibility",
          paragraph:
            "Enjoy the Playbook's diverse range of sports specific drawing tools and field layouts.",
        },
      ],
    },
    {
      section_header: "Video Analysis",
      section_description:
        "Analyse your training and match footage using a variety of analysis tools. Instantly share clips across your network",
      texts: [
        {
          header: "Video Analyser",
          paragraph:
            "Watch your footage, analyse your performance, tag the key situations and give instant feedback to your athletes using XPS Analyser drawing tools.",
        },
        {
          header: "Customised Tagging",
          paragraph:
            "Create customisable tagging formulas and highlight or filter video situations with ease. Import & upload coding data from other video analysers.",
        },
        {
          header: "Presentation Builder",
          paragraph:
            "Build high quality video presentations using images, text fields, voiceover and analyser drawing tools.",
        },
      ],
    },
    {
      section_header: "Collections",
      section_description:
        "Store all your know-how in one place and implement your coaching philosophy",
      texts: [
        {
          header: "Drill Collections",
          paragraph:
            "Build your own coaching library with content for your session planning and sharing amongst your coaches and athletes.",
        },
        {
          header: "Exercise Library",
          paragraph:
            "The ingredients to building your workout programs and individual sessions. Including 2000+ exercises provided by XPS Network.",
        },
        {
          header: "Documents Folder",
          paragraph:
            "Upload and securely store your documents & videos in one central place.",
        },
      ],
    },
    {
      section_header: "Injury Management",
      section_description:
        "Report injuries, illnesses & assign treatment plans. Notify your coaching staff in seconds",
      texts: [
        {
          header: "Injury Reporting",
          paragraph:
            "Record injuries, illnesses & important medical information on your players. Create treatment plans, build rehabilitation programs and give detailed feedback to your coaches in seconds.",
        },
        {
          header: "Training Status",
          paragraph:
            "View your players availability, injuries, return dates and overall team training status.",
        },
        {
          header: "Seamless Communication",
          paragraph: "Notify your staff and athletes within a second.",
        },
        {
          header: "Event Log",
          paragraph:
            "Access a complete overview of all current and past injuries, illnesses or treatments.",
        },
      ],
    },
  ],
}

function FeaturesPage(props) {
  const { pageContext } = props
  const languageCode = pageContext && pageContext.lang ? pageContext.lang : "en"
  const {
    texts: { _ },
    headerImgs,
    collectionsImgs,
    communicationImgs,
    diagramsImgs,
    injuryImgs,
    monitoringImgs,
    planningImgs,
    videoAnalysisImgs,
  } = props.data

  _.features = featuresTexts

  const imgs = useMemo(
    () =>
      [
        planningImgs,
        monitoringImgs,
        communicationImgs,
        diagramsImgs,
        videoAnalysisImgs,
        collectionsImgs,
        injuryImgs,
      ].map((sectionImgs) =>
        sectionImgs.nodes.map((img) => ({
          video: img.childImageSharp.fluid.src,
        }))
      ),
    [props.data]
  )

  const faturesPanelData = [
    {
      id: "planning",
      title: _.features.sections[0].section_header,
      icon: PlanningIcon,
    },
    {
      id: "monitoring",
      title: _.features.sections[1].section_header,
      icon: MonitoringIcon,
    },
    {
      id: "communication",
      title: _.features.sections[2].section_header,
      icon: CommunicationIcon,
    },
    {
      id: "tactics",
      title: _.features.sections[3].section_header,
      icon: DiagramIcon,
    },
    {
      id: "video_analysis",
      title: _.features.sections[4].section_header,
      icon: VideoAnalysisIcon,
    },
    {
      id: "collections",
      title: _.features.sections[5].section_header,
      icon: CollectionsIcon,
    },
    {
      id: "injury_management",
      title: _.features.sections[6].section_header,
      icon: InjuryIcon,
    },
  ]

  return (
    <Layout
      header={
        <Header
          num={1}
          translations={_}
          headerFullYOffset={600}
          mobileHeaderFullYOffset={40}
          fixed
          languageCode={languageCode}
        />
      }
      translations={_}
      languageCode={languageCode}
    >
      <FeaturesPanel data={faturesPanelData} />
      <Landing
        heading={_.features.title}
        descriptionItems={_.features.sections.map((f) => f.section_header)}
        image={headerImgs.edges[0]}
        actionButton={
          <Button
            classStyle={styles.actionButton}
            onClick={() => scrollTo(`#${faturesPanelData[0].id}`)}
          >
            SEE ALL FEATURES
          </Button>
        }
      />
      <div>
        {_.features.sections.map((f, i) => (
          <div
            className={["feature-section", styles.featureSection].join(" ")}
            key={f.section_header}
            id={faturesPanelData[i].id}
          >
            <TrackVisibility partialVisibility key={i} offset={600}>
              {({ isVisible }) => (
                <FeatureTMP
                  key={i}
                  isVisible={isVisible}
                  dividerColor={styles.green}
                  videos={imgs[i]}
                  left={i % 2 === 0}
                  texts={f}
                  slowAnimation
                />
              )}
            </TrackVisibility>
            {(i === 1 || i === 4) && (
              <SeeHowItWorks translations={_} />
            )}
          </div>
        ))}
      </div>
      <GetStarted languageCode={languageCode} translations={_} />
      <Header
        num={2}
        className={globalStyles.hideOnMobile}
        translations={_}
        headerFullYOffset={600}
        boxShadow
        languageCode={languageCode}
      />
      <Footer translations={_} languageCode={languageCode} />
    </Layout>
  )
}

export default FeaturesPage

export const query = graphql`
  query Features($langFile: String) {
    texts: file(relativePath: { eq: $langFile }) {
      _: childTextsYaml {
        ...HeaderTranslations
        ...FooterTranslations
        ...EmailTranslations
        ...TryXpsModalTranslations
        time_to_get_started
        start_free_trial
        ready_to_start
        enter_your_email
        chat {
          full_name
          email
          message
          send_message
          success_message
          success_info
          success
          message_placeholder
        }
      }
    }
    headerImgs: allFile(
      filter: { relativePath: { glob: "features/landing*" } }
      sort: { fields: name }
    ) {
      edges {
        node {
          name
          extension
          relativePath
          childImageSharp {
            fluid(maxWidth: 893) {
              ...GatsbyImageSharpFluid_noBase64
            }
          }
        }
      }
    }
    planningImgs: allFile(
      filter: { relativePath: { glob: "features/planning*" } }
      sort: { fields: name }
    ) {
      nodes {
        name
        extension
        relativePath
        childImageSharp {
          fluid(maxWidth: 893) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
    monitoringImgs: allFile(
      filter: { relativePath: { glob: "features/monitoring*" } }
      sort: { fields: name }
    ) {
      nodes {
        name
        extension
        relativePath
        childImageSharp {
          fluid(maxWidth: 893) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
    communicationImgs: allFile(
      filter: { relativePath: { glob: "features/communication*" } }
      sort: { fields: name }
    ) {
      nodes {
        name
        extension
        relativePath
        childImageSharp {
          fluid(maxWidth: 893) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
    diagramsImgs: allFile(
      filter: { relativePath: { glob: "features/diagrams*" } }
      sort: { fields: name }
    ) {
      nodes {
        name
        extension
        relativePath
        childImageSharp {
          fluid(maxWidth: 893) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
    videoAnalysisImgs: allFile(
      filter: { relativePath: { glob: "features/video_analysis*" } }
      sort: { fields: name }
    ) {
      nodes {
        name
        extension
        relativePath
        childImageSharp {
          fluid(maxWidth: 893) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
    collectionsImgs: allFile(
      filter: { relativePath: { glob: "features/collections*" } }
      sort: { fields: name }
    ) {
      nodes {
        name
        extension
        relativePath
        childImageSharp {
          fluid(maxWidth: 893) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
    injuryImgs: allFile(
      filter: { relativePath: { glob: "features/injury*" } }
      sort: { fields: name }
    ) {
      nodes {
        name
        extension
        relativePath
        childImageSharp {
          fluid(maxWidth: 893) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
    }
  }
`
